import { CircularProgress, Typography, Stack } from '@mui/material';
import { makeStyles } from '@mui/styles';
import Autocomplete from '@mui/material/Autocomplete';
import React, { useEffect, useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import Button from '@mui/material/Button';
import RefreshIcon from '@mui/icons-material/Refresh';
import { textFieldStyles } from '../../Configurations/ExpenseEntryConfig';
import { valueAsNumber } from '../../Utils/ValueAsUtils';
import ExpenseTextField from './ExpenseTextField';
import { COLORS } from '../../../utils/Application_Constants';
import { refreshVendors } from '../../Services/ExpenseService';

const styles = makeStyles((theme) => {
    const baseStyles = textFieldStyles(theme);
    return {
        ...baseStyles,
        autoCompleteInputRoot: {
            paddingBottom: '0px !important',
            paddingRight: '16px !important',
        },
        autoCompleteInput: {
            ...baseStyles.input,
        },
        autoCompletePaper: {
            fontWeight: 'normal',
        },
        autoCompleteListbox: {
            paddingBottom: '0 !important',
        },
        autoCompleteClear: {
            marginRight: '8px',
        },
        refreshIcon: {
            marginRight: theme.spacing(1),
            color: COLORS.LT_JOURNEY,
        },
        refreshButton: {
            width: '100%',
            alignItems: 'left',
            display: 'flex',
        },
        refreshText: {
            width: '100%',
            alignItems: 'left',
            display: 'flex',
            color: COLORS.LT_MIDNIGHT50,
            fontWeight: 400,
        },
        refreshStickyFooter: {
            position: 'sticky',
            bottom: 0,
            backgroundColor: COLORS.LT_MIDNIGHT5,
            borderTop: '1px solid #ddd',
            zIndex: 1,
            width: '100%',
            margin: 0,
        },
    };
});

const preventAutocompleteBlur = (event) => {
    event.preventDefault();
    event.stopPropagation();
};

const CustomListbox = React.forwardRef((props, ref) => {
    const { children, refreshLabel, isLoading, cancelLabel, onCancel, onRefresh, ...rest } = props;
    const classes = styles();

    return (
        <ul {...rest} ref={ref}>
            {children}
            <Stack
                direction="row"
                className={classes.refreshStickyFooter}
                onMouseDown={preventAutocompleteBlur}
                component="li"
                role="presentation"
            >
                <Button
                    onMouseDown={preventAutocompleteBlur}
                    onClick={onRefresh}
                    fullWidth
                    sx={{ gap: 1, justifyContent: 'flex-start' }}
                >
                    {isLoading ? <CircularProgress color="inherit" size={20} /> : <RefreshIcon />}
                    <Typography
                        sx={{
                            fontSize: 'inherit',
                            fontWeight: 500,
                            width: '100%',
                            textAlign: 'left',
                            color: 'inherit',
                        }}
                    >
                        {refreshLabel}
                    </Typography>
                </Button>
                {cancelLabel && (
                    <Button variant="text" onMouseDown={preventAutocompleteBlur} onClick={onCancel}>
                        {cancelLabel}
                    </Button>
                )}
            </Stack>
        </ul>
    );
});

CustomListbox.displayName = 'CustomListbox';

export default function ExpenseAutocomplete(props) {
    const classes = styles();
    const {
        startAdornment,
        label,
        error,
        helperText,
        required,
        onChange,
        value,
        minLength = 1,
        inputProps,
        getOptionsMethod,
        optionLabelKey,
        optionSelectedKey,
        valueAsMethod,
        placeholder,
        showRefreshVendorButton = false,
        isViewOnly = false,
    } = props;
    const excludedProps = [
        'startAdornment',
        'error',
        'helperText',
        'required',
        'inputProps',
        'minLength',
        'getOptionsMethod',
        'optionLabelKey',
        'optionSelectedKey',
        'valueAsMethod',
        'isViewOnly',
        'showRefreshVendorButton',
    ];
    const includedProps = Object.keys(props).reduce((acc, key) => {
        const result = acc;
        if (!excludedProps.includes(key)) {
            const { [key]: propValue } = props;
            result[key] = propValue;
        }
        return result;
    }, {});

    const [open, setOpen] = useState(false);
    const [options, setOptions] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const defaultNoOptionsText = `Please enter a minimum of ${minLength} character(s)`;
    const [noOptionsText, setNoOptionsText] = useState(defaultNoOptionsText);
    const [isLoading, setIsLoading] = useState(false);

    const [refreshValue, setRefreshValue] = useState('Refresh');
    const [cancelValue, setCancelValue] = useState('');
    const [isFirstSearch, setIsFirstSearch] = useState(true);
    const inputValueRef = useRef(inputValue);
    const isCancelledRef = useRef(false);

    inputValueRef.current = inputValue;

    const handleSearch = async (
        searchTerm = inputValueRef.current,
        { fromRefresh = false } = {}
    ) => {
        setIsLoading(true);
        try {
            const response = await getOptionsMethod(searchTerm);
            // Only the refresh follow-up search honors Cancel. Typed searches must
            // still update after a prior refresh was cancelled (ref stays true).
            if (fromRefresh && isCancelledRef.current) {
                return;
            }
            setOptions(Array.isArray(response) ? response : [response]);
            setIsFirstSearch(false);
        } catch (_error) {
            // Keep current options so a failed search does not blank the list.
        } finally {
            setIsLoading(false);
            setNoOptionsText('No options');
        }
    };

    const resetRefreshButton = () => {
        setRefreshValue('Refresh');
        setCancelValue('');
        setIsLoading(false);
    };

    const handleCancel = () => {
        isCancelledRef.current = true;
        resetRefreshButton();
    };

    const isRefreshSuccessful = (response) => {
        if (response?.isSuccessStatusCode === false || response?.IsSuccessStatusCode === false) {
            return false;
        }
        if (response?.isSuccessStatusCode === true || response?.IsSuccessStatusCode === true) {
            return true;
        }

        const status = response?.statusCode ?? response?.StatusCode;
        // Axios already resolved, so a body without a status code is treated as success.
        // Requiring statusCode === 200 skipped the follow-up search when Workato omitted it.
        if (status == null) {
            return true;
        }
        return Number(status) === 200;
    };

    const handleRefresh = async () => {
        const searchTerm = inputValueRef.current;
        setRefreshValue('Refreshing data. May take up to 30 seconds.');
        setCancelValue('Cancel');
        setIsLoading(true);
        isCancelledRef.current = false;

        try {
            const response = await refreshVendors();

            if (isCancelledRef.current) {
                resetRefreshButton();
                return;
            }
            if (!isRefreshSuccessful(response)) {
                resetRefreshButton();
                setRefreshValue('Try again. Failed to refresh data.');
                return;
            }

            setOpen(true);
            await handleSearch(searchTerm, { fromRefresh: true });

            if (isCancelledRef.current) {
                resetRefreshButton();
                return;
            }
            resetRefreshButton();
        } catch (_error) {
            if (isCancelledRef.current) {
                resetRefreshButton();
                return;
            }
            resetRefreshButton();
            setRefreshValue('Try again. Failed to refresh data.');
        }
    };

    const debounced = useDebouncedCallback((input) => {
        if (input.length >= minLength && open) {
            handleSearch();
        } else if (!input) {
            setOptions([]);
        }
    }, 1000);

    useEffect(() => {
        if (!open) {
            setOptions([]);
        }
    }, [open]);

    const handleInputChange = (e, newInputValue, reason) => {
        // TDS-8120 leaving this commented out in case someone changes their mind on this functionatily
        // This solves: After searching for a booking number/clinician id, if I highlight the number to overwrite it, the first number I select is ignored.
        // However, booking details will not be changed until a new booking is selected or the field is cleared. Tested that booking and clinician ids can't be mismatched
        // if (reason && reason === 'input' && value) {
        //	onChange(null);
        //	setOptions([]);
        // }
        if (!newInputValue) {
            setNoOptionsText(defaultNoOptionsText);
        }
        if (reason !== 'reset') {
            debounced(newInputValue);
        }
        if (valueAsMethod) {
            setInputValue(valueAsNumber(newInputValue));
        } else {
            setInputValue(newInputValue);
        }
    };

    const renderEndAdornment = () => {
        if (isLoading) {
            return <CircularProgress color="inherit" size={20} />;
        }

        if (
            showRefreshVendorButton &&
            !isFirstSearch &&
            options.length === 0 &&
            inputValue.length >= minLength
        ) {
            return (
                <Button onMouseDown={preventAutocompleteBlur} onClick={handleRefresh}>
                    <RefreshIcon className={classes.refreshIcon} />
                    <Typography className={classes.refreshText}>{refreshValue}</Typography>
                </Button>
            );
        }

        return null;
    };

    return (
        <Autocomplete
            {...includedProps}
            classes={{
                inputRoot: classes.autoCompleteInputRoot,
                input: classes.autoCompleteInput,
                paper: classes.autoCompletePaper,
                listbox: showRefreshVendorButton ? classes.autoCompleteListbox : undefined,
                clearIndicator: classes.autoCompleteClear,
                disabled: classes.disabled,
            }}
            open={open}
            onOpen={() => {
                setOpen(true);
            }}
            onClose={() => {
                setOpen(false);
            }}
            noOptionsText={noOptionsText}
            options={options}
            onChange={(e, newValue) => onChange(newValue)}
            onInputChange={(e, newInputValue, reason) => {
                handleInputChange(e, newInputValue, reason);
            }}
            disabled={isViewOnly}
            value={value || null}
            inputValue={inputValue}
            loading={isLoading}
            filterOptions={showRefreshVendorButton ? (opts) => opts : undefined}
            getOptionLabel={(option) =>
                option[optionLabelKey]?.toString() || option.toString() || ''
            }
            isOptionEqualToValue={(option, testValue) =>
                option === testValue || `${option[optionSelectedKey]}` === testValue
            }
            renderInput={(params) => {
                const {
                    InputProps: autocompleteInputProps,
                    inputProps: autocompleteHtmlInputProps,
                    InputLabelProps: autocompleteInputLabelProps,
                    ...restParams
                } = params;
                return (
                    <ExpenseTextField
                        {...restParams}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && options.length === 1) {
                                onChange(options[0]);
                                setOpen(false);
                                e.preventDefault();
                            }
                        }}
                        label={label}
                        placeholder={placeholder}
                        slotProps={{
                            inputLabel: {
                                ...autocompleteInputLabelProps,
                                shrink: true,
                                classes: {
                                    asterisk: classes.inputLabelAsterisk,
                                },
                            },
                            input: {
                                ...autocompleteInputProps,
                                ...inputProps,
                                classes: {
                                    root: classes.inputRoot,
                                    error: classes.error,
                                    disabled: classes.disabled,
                                },
                                startAdornment: startAdornment && startAdornment,
                                endAdornment: renderEndAdornment(),
                            },
                            htmlInput: {
                                ...autocompleteHtmlInputProps,
                                ...inputProps,
                            },
                        }}
                        required={required}
                        error={error}
                        helperText={helperText}
                    />
                );
            }}
            slotProps={
                showRefreshVendorButton
                    ? {
                          listbox: {
                              component: CustomListbox,
                              onRefresh: handleRefresh,
                              refreshLabel: refreshValue,
                              cancelLabel: cancelValue,
                              onCancel: handleCancel,
                              isLoading,
                          },
                      }
                    : undefined
            }
        />
    );
}
